import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { IDateFilterParams, IServerSideGetRowsRequest } from "ag-grid-enterprise";
import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useState } from "react";
import GenericProGrid from "../../../components/GenericProGrid/GenericProGrid";
import { uiActions } from "../../../reducers/uiReducer";
import { CustomTypeColumnDefinition, isOnlySingleRowSelected } from "../../../utils/AGGridUtils";
import SelectValueListCustomFilter, {
    IApiValueListFilterConfig,
} from "../Campaign/components/CampaignDetailsPage/components/CampaignJourneyGrid/SelectValueListCustomFilter";
import { DATE_FILTER_OPTIONS, NUMBER_FILTER_OPTIONS, STRING_FILTER_OPTIONS } from "../../../utils/OrderGridUtils/OrderGridUtils";
import GridHighlightedValue from "../../../components/OrderGridV3/GridHighlightedValue/GridHighlightedValue";
import { DatesFormat } from "../../../utils/DateFormats";
import { V3ColumnOption } from "../../../utils/OrderGridUtils/types";
import { BO_CORE_DEFAULT_VALUE, DEFAULT_BOCORE_DATE, info, success, toastError } from "../../../utils/AppUtils";
import { getLabelTagDetails, getLabelTags, updateLabelTag } from "../../../config/axios/apis/labelTags/labelTagsAPI";
import { utcToLocalDateTime } from "../../../utils/DateTimeUtils";
import CreateLabelModal from "../../../components/Common/CreateLabelModal/CreateLabelModal";
import { getTypeByKey } from "../../../config/axios/apis/type/type";
import { CACHE_DURATION, STALE_DURATION } from "../DataView/utils/constants";
import { LabelDetails } from "../../../config/axios/apis/labelTags/types";
import MigrateLabelModal, { IMigratableLabel } from "../Applications/MigrateLabelModal/MigrateLabelModal";
import CountCellRenderer from "./CountCellRenderer";
import StatusCellRenderer from "./StatusCellRenderer";

const filterOptionsMap = {
    id: NUMBER_FILTER_OPTIONS,
    name: STRING_FILTER_OPTIONS,
    count: NUMBER_FILTER_OPTIONS,
    category: STRING_FILTER_OPTIONS,
    created: DATE_FILTER_OPTIONS,
    updated: DATE_FILTER_OPTIONS,
};

const getAllALabelTags = (config?: any) => {
    return getLabelTags({}, { ...config, params: { ...config.params } }).then((res) => {
        (res.data.data as any[]) = res.data.data.map((e) => ({
            id: e.id,
            isActive: e.isActive,
            name: e.name !== BO_CORE_DEFAULT_VALUE ? e.name || "" : "",
            type: e.type !== BO_CORE_DEFAULT_VALUE ? e.type || "" : "",
            count: null,
            created: e.created && e.created !== DEFAULT_BOCORE_DATE ? utcToLocalDateTime(e.created, true) : "",
            updated: e.updated && e.updated !== DEFAULT_BOCORE_DATE ? utcToLocalDateTime(e.updated, true) : "",
        }));
        return res;
    });
};

const requestFilterProcessorV3 = (params: IServerSideGetRowsRequest) => {
    const filterModelKeys = Object.keys(params.filterModel || {});

    if (filterModelKeys.length === 0) return [];

    const result = filterModelKeys.map((key) => {
        const filterItem = params.filterModel![key];
        if (filterItem.filterType === "date") {
            if (filterItem.type === "inRange") {
                return {
                    key,
                    value: {
                        startDate: filterItem.dateFrom?.split(" ")[0],
                        endDate: filterItem.dateTo?.split(" ")[0],
                    },
                    operator: filterItem.type,
                };
            }
            return {
                key,
                value: filterItem.dateFrom?.split(" ")[0],
                operator: filterItem.type,
            };
        }
        return {
            key,
            ...(filterItem.filter ? { value: filterItem.filter } : {}),
            operator: filterItem.type,
        };
    });

    return result.filter(Boolean);
};

const LabelsDataDefinition: CustomTypeColumnDefinition[] = [
    { attrKey: "id", atrbType: "number", name: "ID" },
    { attrKey: "name", atrbType: "text", name: "Name" },
    { attrKey: "count", atrbType: "number", name: "Count" },
    { attrKey: "type", atrbType: "text", name: "Category" },
    { attrKey: "isActive", atrbType: "boolean", name: "Status" },
    { attrKey: "created", atrbType: "timestamp", name: "Created" },
    { attrKey: "updated", atrbType: "timestamp", name: "Updated" },
];

const columnOptions = [
    {
        key: "id",
        options: {
            filterParams: { filterOptions: filterOptionsMap.id },
            cellRenderer: (value, params) => (
                <span>
                    <GridHighlightedValue {...params} value={value} columnKey="id" enableCopyToClipboard />{" "}
                </span>
            ),
        },
    },
    {
        key: "name",
        options: {
            filterParams: { filterOptions: filterOptionsMap.name },
            cellRenderer: (value, rendererParams) => (
                <span>
                    <GridHighlightedValue
                        {...rendererParams}
                        columnKey="name"
                        value={(value || value === 0) && value !== "" ? value : ""}
                        enableCopyToClipboard
                    />
                </span>
            ),
        },
    },
    {
        key: "type",
        options: {
            filter: { component: SelectValueListCustomFilter },
            cellRenderer: (value, rendererParams) => (
                <span>
                    <GridHighlightedValue
                        {...rendererParams}
                        columnKey="type"
                        value={(value || value === 0) && value !== "" ? value : ""}
                        enableCopyToClipboard
                    />
                </span>
            ),
        },
    },
    {
        key: "isActive",
        options: {
            sortable: false,
            filter: false,
            floatingFilter: false,
            cellRenderer: (value, rendererParams) => <StatusCellRenderer value={value} rendererParams={rendererParams} />,
        },
    },
    {
        key: "created",
        options: {
            filterParams: {
                filterOptions: filterOptionsMap.created,
                maxValidYear: 2100,
                minValidYear: 1900,
                inRangeFloatingFilterDateFormat: DatesFormat.defaultUSFormat,
            } as IDateFilterParams,
        },
    },
    {
        key: "updated",
        options: {
            filterParams: {
                filterOptions: filterOptionsMap.updated,
                maxValidYear: 2100,
                minValidYear: 1900,
                inRangeFloatingFilterDateFormat: DatesFormat.defaultUSFormat,
            } as IDateFilterParams,
        },
    },
    {
        key: "count",
        options: {
            filter: false,
            floatingFilter: false,
            sortable: false,
            context: { suppressCsvExport: true, suppressExcelExport: true },
            cellRenderer: CountCellRenderer,
        },
    },
] as V3ColumnOption[];

const LabelManagement = () => {
    const [labelDetails, setLabelDetails] = useState<LabelDetails | null>(null);
    const [targetLabelForStatusUpdate, setTargetLabelForStatusUpdate] = useState<any>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [migrateLabelInstance, setMigrateLabelInstance] = useState<IMigratableLabel | null>(null);
    const [isMigrateModalOpen, setIsMigrateModalOpen] = useState(false);
    const dispatch = useDispatch();

    // NOTE: implement useValueListOptions hook for fetching categories;
    const { data: categoryOptions = [] } = useQuery({
        queryKey: ["bocoreType", "LabelTag"],
        gcTime: CACHE_DURATION,
        staleTime: STALE_DURATION,
        queryFn: (ctx) => getTypeByKey(ctx.queryKey[1], false, { signal: ctx.signal }),
        select: (res) => {
            if (!res?.data?.attributes) return [];

            const typeAttr = res.data.attributes.find((att) => att.attrKey === "type");

            if (!typeAttr?.attributeValues) return [];

            return typeAttr.attributeValues.filter((v) => v.name !== BO_CORE_DEFAULT_VALUE).map((v) => ({ value: v.name, label: v.name }));
        },
    });

    const singleRowAdditionalOptions = (selectedRow, shouldEnabled = true) => {
        const row = selectedRow?.[0];
        const isRowSelected = selectedRow?.length === 1 && shouldEnabled;
        return [
            {
                name: "Migrate",
                action: async () => {
                    try {
                        let { count } = row;
                        if (count === null || count === undefined) {
                            const res = await getLabelTagDetails(row.id, {}, true);
                            count = res?.data?.taggedInstancesCount || 0;
                        }

                        if (count === 0) {
                            info(<FormattedMessage id="label-tags.info.migrate" defaultMessage="Selected label has no tagged instances to migrate." />);
                            return;
                        }

                        setMigrateLabelInstance({
                            id: row.id,
                            name: row.name,
                            usageCount: count,
                            type: row.type,
                        });
                        setIsMigrateModalOpen(true);
                    } catch (error: any) {
                        toastError(error?.response?.data?.message || error?.message);
                    }
                },
                disabled: !isRowSelected,
            },
            {
                name: "Enable",
                action: () => setTargetLabelForStatusUpdate(row),
                disabled: !(isRowSelected && !row?.isActive),
                hide: row?.isActive,
            },
            {
                name: "Disable",
                action: () => setTargetLabelForStatusUpdate(row),
                disabled: !(isRowSelected && row?.isActive),
                hide: !row?.isActive,
            },
        ];
    };

    const handleOpenCreate = () => {
        setLabelDetails(null);
        setIsOpen(true);
    };

    const handleCloseDialog = () => setTargetLabelForStatusUpdate(null);

    const handleConfirmToggleStatus = async () => {
        if (!targetLabelForStatusUpdate) return;

        const newStatus = !targetLabelForStatusUpdate.isActive;

        try {
            await updateLabelTag(targetLabelForStatusUpdate.id, {
                active: newStatus,
            });

            const successMsg = newStatus ? "Label tag has been successfully enabled" : "Label tag has been successfully disabled";

            success(<FormattedMessage id={`label-tags.success.${newStatus ? "enable" : "disable"}-tag`} defaultMessage={successMsg} />);

            dispatch(uiActions.setForceRefreshGrid(true));
            handleCloseDialog();
        } catch (error: any) {
            toastError(error?.response?.data?.message || error?.message);
        }
    };

    return (
        <Paper sx={{ p: 2, my: 2 }}>
            <Stack
                direction="row"
                sx={{
                    justifyContent: "flex-end",
                    mb: 2,
                }}
            >
                <Button variant="CTA" onClick={handleOpenCreate}>
                    <FormattedMessage id="common.button-label.create-label" defaultMessage="Create Label" />
                </Button>
            </Stack>
            <GenericProGrid
                gridIdentifier="labelManagement"
                dataFetchingType="restApi"
                customTypeDefinition={LabelsDataDefinition}
                restApiRequest={getAllALabelTags}
                disableFullTextSearch
                disableSearchParamsV3
                columnOptions={columnOptions}
                tableTitle="Label Management"
                showIdColumn={{ show: true }}
                editLink={(row) => {
                    const details = {
                        id: row.id,
                        name: row.name,
                        category: { value: row.type, label: row.type },
                    };

                    setLabelDetails(details);
                    setIsOpen(true);
                }}
                requestFilterProcessorV3={requestFilterProcessorV3}
                customGridOptions={{
                    context: {
                        customListFilterMap: {
                            type: { fetchType: "api.valueList", bocoreTypeName: "LabelTag", fieldName: "type" } as IApiValueListFilterConfig,
                        },
                    },
                    getContextMenuItems: (params) => {
                        const selectedRow = [params?.node?.data];
                        const singleRowData = isOnlySingleRowSelected(params?.api);
                        return singleRowAdditionalOptions(selectedRow, singleRowData);
                    },
                }}
            />
            <CreateLabelModal open={isOpen} labelDetails={labelDetails} categories={categoryOptions} onClose={() => setIsOpen(false)} />
            {migrateLabelInstance && (
                <MigrateLabelModal
                    open={isMigrateModalOpen}
                    labelInstance={migrateLabelInstance}
                    onClose={() => {
                        setIsMigrateModalOpen(false);
                        setMigrateLabelInstance(null);
                    }}
                />
            )}
            <Dialog open={Boolean(targetLabelForStatusUpdate)} onClose={handleCloseDialog}>
                <DialogTitle>
                    {targetLabelForStatusUpdate?.isActive ? (
                        <FormattedMessage id="label-tags.title.disable" defaultMessage="Disable Label" />
                    ) : (
                        <FormattedMessage id="label-tags.title.enable" defaultMessage="Enable Label" />
                    )}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        <FormattedMessage
                            id={targetLabelForStatusUpdate?.isActive ? "label-tags.message.disable-confirm" : "label-tags.message.enable-confirm"}
                            defaultMessage={
                                targetLabelForStatusUpdate?.isActive
                                    ? "Are you sure you want to disable label: {name}?"
                                    : "Are you sure you want to enable label: {name}?"
                            }
                            values={{ name: <b>{targetLabelForStatusUpdate?.name}</b> }}
                        />
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog}>
                        <FormattedMessage id="common.button-label.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button onClick={handleConfirmToggleStatus} color={targetLabelForStatusUpdate?.isActive ? "error" : "inherit"}>
                        <FormattedMessage id="common.button-label.confirm" defaultMessage="Confirm" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default LabelManagement;
